import React, { useState, useEffect, useCallback, useMemo } from "react";
import TinyEditor from "../../../custom-ckeditor/TinyEditor";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";
import QuizPage from "../../quiz/QuizPage";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const createEmptyItem = () => ({
  Type: "Iraab",
  Name: "",
  Title: "",
  points: 1,
  skilltype: "",
  itemcat: "",
  Iraab: { id: 1, Rationale: "", Value: "", Direction: "RTL" },
  version: "v1",
});

const getItemIdFromAny = (res, fallback) =>
  res?.item?.Id ??
  res?.item?.id ??
  res?.item?.itemId ??
  res?.Id ??
  res?.id ??
  res?.itemId ??
  fallback?.Id ??
  fallback?.id ??
  fallback?.itemId ??
  null;

const IraabItemDetail = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const isNew = !itemId || itemId === "new";
  const location = useLocation();
  const clonedItem = location.state?.clonedItem || null;
  const [item, setItem] = useState(createEmptyItem());
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false); // ✅ added
  const [effectiveItemId, setEffectiveItemId] = useState(
    clonedItem ? null : isNew ? null : itemId
  );
  // 🟢 Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [taxonomyResponse, itemResponse] = await Promise.all([
          ItemService.fetchTaxonomy("arabic"),
          isNew ? Promise.resolve(null) : ItemService.fetchItemById(itemId),
        ]);

        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        let loaded = null;

        // 1️⃣ Clone Mode
        if (clonedItem) {
          loaded = {
            ...clonedItem,

            id: undefined,
            Id: undefined,
            PK: undefined,
            SK: undefined,
            GPK1: undefined,
            GSK1: undefined,
            json: undefined,
            Json: undefined,

            createdBy: "",
            updatedBy: "",
            version: "v1",

            // Keep name as "(Copy)" from ItemLibrary
            Name: clonedItem.Name,

            Iraab: {
              id: 1,
              Value: clonedItem.Iraab?.Value || "",
              Rationale: clonedItem.Iraab?.Rationale || "",
              Direction: clonedItem.Iraab?.Direction || "RTL",
            },
          };

          setEffectiveItemId(null); // Force new item create mode
        }

        // 2️⃣ Edit existing
        else if (itemResponse?.item) {
          const ex = itemResponse.item;

          loaded = {
            Type: ex.Type || "Iraab",
            Name: ex.Name || "",
            Title: ex.Title || "",
            points: ex.points ?? 1,
            skilltype: ex.skilltype || "",
            itemcat: ex.itemcat || "",
            Iraab: {
              id: 1,
              Value: ex.Iraab?.Value || "",
              Rationale: ex.Iraab?.Rationale || "",
              Direction: ex.Iraab?.Direction || "RTL",
            },
            version: "v1",
          };
        }

        // 3️⃣ New Item
        else {
          loaded = createEmptyItem();
        }

        setItem(loaded);
      } catch (err) {
        console.error("Failed to load Iraab item:", err);
        setSaveError("Failed to load Iraab item.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [itemId, isNew, clonedItem]);

  const handleCloneFromEditor = useCallback(() => {
    if (!item) return;

    const clonedPayload = {
      ...item,

      // 🔥 remove identifiers
      id: undefined,
      Id: undefined,
      itemId: undefined,
      ItemId: undefined,
      PK: undefined,
      SK: undefined,
      GPK1: undefined,
      GSK1: undefined,

      // metadata reset
      Name: `${item.Name || "Untitled Iraab"} (Copy)`,
      createdBy: "",
      updatedBy: "",
      version: "v1",

      // JSON rebuilt on save
      json: undefined,
      Json: undefined,

      // 🔥 deep copy Iraab
      Iraab: {
        id: 1,
        Value: item.Iraab.Value,
        Rationale: item.Iraab.Rationale || "",
        Direction: item.Iraab.Direction || "RTL",
      },
    };

    navigate("/authoring/items/iraab/item/new", {
      state: { clonedItem: clonedPayload },
    });
  }, [item, navigate]);

  const handleFieldChange = (key, value) =>
    setItem((prev) => ({ ...prev, [key]: value }));

  const handleIraabChange = (field, value) =>
    setItem((prev) => ({
      ...prev,
      Iraab: { ...prev.Iraab, [field]: value },
    }));

  // 🟢 Save function
  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    const timestamp = new Date().toISOString();

    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: effectiveItemId ? `item#${effectiveItemId}` : undefined,
      GPK1: "item",
      GSK1: effectiveItemId ? `item#${effectiveItemId}` : undefined,
      Type: "Iraab",
      Name: item.Name,
      Title: item.Title,
      points: item.points,
      itemcat: item.itemcat,
      skilltype: item.skilltype,
      version: "v1",
      dateCreated: item.dateCreated || timestamp,
      dateUpdated: timestamp,
      Iraab: {
        id: 1,
        Value: item.Iraab.Value,
        Rationale: item.Iraab.Rationale,
      },
    };
    try {
      setSaving(true);
      const response = await ItemService.saveItem(
        payload,
        effectiveItemId || undefined
      );

      setSaveSuccess(true);
      setSaveError(null);

      // 🔥 After first save -> switch to edit mode
      const newId = getItemIdFromAny(response, payload);

      if (!effectiveItemId && newId) {
        setEffectiveItemId(newId);
      }
    } catch (err) {
      console.error("Save error:", err);
      setSaveError("Failed to save Iraab item.");
    } finally {
      setSaving(false);
    }
  }, [item, effectiveItemId]);

  const jsonPreview = useMemo(() => JSON.stringify(item, null, 2), [item]);

  if (loading)
    return (
      <Paper sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading Iraab item...</Typography>
      </Paper>
    );

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="Iraab" color="secondary" />
              <Typography variant="h5" fontWeight={600}>
                {isNew ? "Create Iraab Item" : "Edit Iraab Item"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={!effectiveItemId} // 🔥 only in edit mode
                onClick={handleCloneFromEditor}
              >
                Clone
              </Button>

              <Button
                variant="outlined"
                onClick={() => setPreviewDialogOpen(true)}
              >
                Preview
              </Button>

              <Button
                variant="outlined"
                onClick={() => setJsonDialogOpen(true)}
              >
                View JSON
              </Button>
            </Stack>
          </Stack>

          {saveSuccess && (
            <Alert severity="success" onClose={() => setSaveSuccess(false)}>
              Iraab item saved successfully!
            </Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          <Divider />

          {/* === Basic Info === */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                fullWidth
                required
                value={item.Name}
                onChange={(e) => handleFieldChange("Name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Points"
                type="number"
                fullWidth
                value={item.points}
                onChange={(e) => handleFieldChange("points", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Skill Type</InputLabel>
                <Select
                  value={item.skilltype}
                  label="Skill Type"
                  onChange={(e) =>
                    handleFieldChange(
                      "skilltype",
                      normalizeSkillTypeValue(e.target.value)
                    )
                  }
                >
                  {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Item Category</InputLabel>
                <Select
                  value={item.itemcat}
                  label="Item Category"
                  onChange={(e) => handleFieldChange("itemcat", e.target.value)}
                >
                  {taxonomyOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider />

          <Typography variant="subtitle1" fontWeight={600}>
            Instructions (Title)
          </Typography>

          <TinyEditor
            value={item.Title || ""}
            onChange={(value) => handleFieldChange("Title", value)}
            minHeight={250}
          />

          <Divider />

          <Typography variant="subtitle1" fontWeight={600}>
            Iraab Text
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={item.Iraab.Direction === "RTL"}
                onChange={() =>
                  handleIraabChange(
                    "Direction",
                    item.Iraab.Direction === "RTL" ? "LTR" : "RTL"
                  )
                }
              />
            }
            label="Direction"
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            value={item.Iraab.Value}
            onChange={(e) => handleIraabChange("Value", e.target.value)}
            placeholder="Enter Arabic sentence(s)..."
            InputProps={{
              sx: {
                direction: item.Iraab.Direction === "RTL" ? "rtl" : "ltr",
                textAlign: item.Iraab.Direction === "RTL" ? "right" : "left",
                fontFamily: "Traditional Arabic, serif",
                fontSize: "1.2rem",
              },
            }}
          />

          <Typography variant="subtitle1" fontWeight={600}>
            Rationale
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={item.Iraab.Rationale}
            onChange={(e) => handleIraabChange("Rationale", e.target.value)}
          />

          <Stack direction="row" justifyContent="flex-end">
            <Button
              startIcon={effectiveItemId ? <SaveIcon /> : <SaveIcon />}
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? effectiveItemId
                  ? "Saving..."
                  : "Creating..."
                : effectiveItemId
                ? "Save"
                : "Create"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* === JSON Dialog === */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* === Preview Dialog === */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Iraab Preview</DialogTitle>
        <DialogContent dividers>
          <QuizPage
            previewMode
            previewActivities={[
              {
                ...item,
                ContentId: "preview",
              },
            ]}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default IraabItemDetail;
